import type { FlattenSimpleInterpolation } from 'styled-components';
import styled from 'styled-components';
import {
  AudiCopy1WithHoverStyle,
  generateMobileMenuMaxHeightCss,
  timeHeaderTransitionDelay,
  timeM,
  timeXL,
} from '../AudiHeader/AudiHeaderStyleUtils';
import type { HeaderStyledSubNavProps } from '../../interfaces/header-styled-components.interfaces';
import { createBreakpointStyles } from '../../services';
import { responsiveStyles } from '../../utils/responsiveStyles';
import type { CustomTheme } from '../../../@types/theme';

const headerSubNavStyles = (
  theme: CustomTheme,
  isSubNavOpen: boolean,
): Array<{ maxNavItems: number; style: unknown }> => [
  {
    maxNavItems: 0,
    style: null,
  },
  {
    maxNavItems: 4,
    style: {
      bottom: { l: 'initial' },
      height: {
        l: isSubNavOpen ? 'auto' : '0',
      },
      'max-height': {
        l: `${
          isSubNavOpen ? '660px' : '0'
        } /* 660px => teaser-image height * 2 - so it leaves space for about 8 cells of nav items in total */`,
      },
      opacity: { l: isSubNavOpen ? '1' : '0' },
      overflow: { l: 'hidden' },
      'overflow-y': { l: 'initial' },
      padding: {
        xl: '0 var(--one-header-side-spacing) 0 var(--one-header-flyout-left-spacing)',
      },
      top: { l: '72px' },
      transform: { l: 'none' },
      transition: {
        l: isSubNavOpen
          ? `max-height ${timeXL} ${theme.easing.easingDefault}, opacity ${timeM} ${theme.easing.easingDefault} ${timeHeaderTransitionDelay}`
          : `max-height ${theme.easing.easingDefault} ${timeXL}, opacity ${timeM} ${theme.easing.easingDefault}`,
      },
      'z-index': { l: '-1' },
    },
  },
  {
    maxNavItems: 5,
    style: {
      bottom: { xl: 'initial' },
      height: {
        xl: isSubNavOpen ? 'auto' : '0',
      },
      'max-height': {
        xl: `${
          isSubNavOpen ? '660px' : '0'
        } /* 660px => teaser-image height * 2 - so it leaves space for about 8 cells of nav items in total */`,
      },
      opacity: { xl: isSubNavOpen ? '1' : '0' },
      overflow: { xl: 'hidden' },
      'overflow-y': { xl: 'initial' },
      padding: {
        xl: '0 var(--one-header-side-spacing) 0 var(--one-header-flyout-left-spacing)',
      },
      top: { xl: '72px' },
      transform: { xl: 'none' },
      transition: {
        xl: isSubNavOpen
          ? `max-height ${timeXL} ${theme.easing.easingDefault}, opacity ${timeM} ${theme.easing.easingDefault} ${timeHeaderTransitionDelay}`
          : `max-height ${timeXL} ${theme.easing.easingDefault}, opacity ${timeM} ${theme.easing.easingDefault}`,
      },
      'z-index': { xl: '-1' },
    },
  },
  {
    maxNavItems: 8,
    style: {
      bottom: { xxl: 'initial' },
      height: {
        xxl: isSubNavOpen ? 'auto' : '0',
      },
      'max-height': {
        xxl: `${
          isSubNavOpen ? '660px' : '0'
        } /* 660px => teaser-image height * 2 - so it leaves space for about 8 cells of nav items in total */`,
      },
      opacity: { xxl: isSubNavOpen ? '1' : '0' },
      overflow: { xxl: 'hidden' },
      'overflow-y': { xxl: 'initial' },
      padding: {
        xxl: '0 var(--one-header-side-spacing) 0 var(--one-header-flyout-left-spacing)',
      },
      top: { xxl: '72px' },
      transform: { xxl: 'none' },
      transition: {
        xxl: isSubNavOpen
          ? `max-height ${timeXL} ${theme.easing.easingDefault}, opacity ${timeM} ${theme.easing.easingDefault} ${timeHeaderTransitionDelay}`
          : `max-height ${timeXL} ${theme.easing.easingDefault} ${timeHeaderTransitionDelay}, opacity ${timeM} ${theme.easing.easingDefault}`,
      },
      'z-index': { xxl: '-1' },
    },
  },
  {
    maxNavItems: -1,
    style: null,
  },
];

export const HeaderStyledSubNav = styled.div<HeaderStyledSubNavProps>`
  backface-visibility: hidden;
  background-color: ${({ theme }): string =>
    theme.type === 'legacy'
      ? 'var(--one-header-color-white)'
      : 'var(--one-header-color-canvas-default)'};
  bottom: 0;
  display: block;
  visibility: ${({ isSubNavOpen }): string => (isSubNavOpen ? 'visible' : 'hidden')};
  height: ${({ isSubNavOpen, isDealerSearch }): string =>
    isSubNavOpen ? generateMobileMenuMaxHeightCss(isDealerSearch) : '0'};
  left: 0;
  ${({ headerNavigationItemsAmount, theme, isSubNavOpen }): string =>
    responsiveStyles(
      createBreakpointStyles(headerNavigationItemsAmount, headerSubNavStyles(theme, isSubNavOpen)),
      theme.breakpoints,
    )}
  overflow-y: ${({ isSubNavOpen }): string => (isSubNavOpen ? 'auto' : 'hidden')};
  padding: 0 var(--one-header-side-spacing);
  position: absolute;
  right: 0;
  top: 0;
  transform: ${({ isSubNavOpen }): string =>
    isSubNavOpen ? 'translateX(0)' : 'translateX(100%) scaleY(1)'};
  transition:
    transform ${({ theme }): string => theme.easing.easingDefault} ${timeXL},
    height ${({ theme }): string => theme.easing.easingDefault} ${timeXL};
`;

const headerSubNavButtonStyles = (): Array<{
  maxNavItems: number;
  style: unknown;
}> => [
  {
    maxNavItems: 0,
    style: null,
  },
  {
    maxNavItems: 4,
    style: {
      display: { l: 'none' },
    },
  },
  {
    maxNavItems: 5,
    style: {
      display: { xl: 'none' },
    },
  },
  {
    maxNavItems: 8,
    style: {
      display: { xxl: 'none' },
    },
  },
  {
    maxNavItems: -1,
    style: null,
  },
];

export const HeaderStyledSubNavButton = styled.button<{
  headerNavigationItemsAmount: number;
}>`
  align-items: center;
  background-color: ${({ theme }): string =>
    theme.type === 'legacy'
      ? 'var(--one-header-color-white)'
      : 'var(--one-header-color-canvas-default)'};
  border: none;
  border-bottom: 1px solid #ccc;
  color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-link)'};
  cursor: pointer;
  display: flex;
  flex-flow: row nowrap;
  font-size: 1em;
  ${({ theme }): FlattenSimpleInterpolation => AudiCopy1WithHoverStyle(theme)}
  ${({ headerNavigationItemsAmount, theme }): string =>
    responsiveStyles(
      createBreakpointStyles(headerNavigationItemsAmount, headerSubNavButtonStyles()),
      theme.breakpoints,
    )}
  padding: var(--one-header-space-xl) 0 var(--one-header-space-m) 0;
  width: 100%;

  &:hover,
  &:active,
  &:focus {
    color: ${({ theme }): string =>
      theme.type === 'legacy' ? 'var(--one-header-color-black)' : 'var(--one-header-color-hover)'};
  }

  & svg {
    height: 24px;
    margin-right: var(--one-header-space-s);
    transform: scaleX(-1);
    width: 24px;
  }
`;

export const HeaderStyledSubNavTeaserWrapper = styled.div<{
  headerNavigationItemsAmount: number;
}>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy'
      ? 'var(--one-header-color-white)'
      : '--one-header-color-canvas-default'};
  display: flex;
  margin: var(--one-header-space-xl) 0 0 0;
  ${({ theme }): string =>
    responsiveStyles(
      {
        'flex-direction': {
          m: 'row',
          xs: 'column',
        },
        'justify-content': {
          m: 'space-between',
        },
        margin: {
          xl: 'var(--one-header-space-xxl) 0 0 0;',
        },
      },
      theme.breakpoints,
    )}
  overflow: visible;
`;

export const HeaderStyledSubNavListContainer = styled.nav<{
  headerNavigationItemsAmount: number;
}>`
  margin: 0 0 var(--one-header-space-xxxl);
  ${({ theme }): string =>
    responsiveStyles(
      {
        'column-count': {
          xl: '2',
          xxl: '3',
        },
        'column-gap': {
          xl: 'var(--one-header-space-xxl)',
        },
        'margin-right': {
          m: 'var(--one-header-space-xl)',
        },
      },
      theme.breakpoints,
    )}
`;

export const HeaderStyledSubNavList = styled.ul<{
  headerNavigationItemsAmount: number;
}>`
  list-style: none;
  margin: 0;
  ${({ theme }): string =>
    responsiveStyles(
      {
        width: {
          xl: '320px',
          xs: '100%',
        },
      },
      theme.breakpoints,
    )}
  opacity: 1;
  padding: 0;
`;
